import React from 'react';
import { useMutation } from '@apollo/react-hooks';
import { DocumentNode } from 'graphql';
import { Button } from '@material-ui/core';

import { ListCard, Item } from 'components/Card';
import ActionMenu, { ActionType } from './ActionMenu';
import { MoveDirection } from 'types';

interface Props<T> {
  title: string;
  itunesId: string;
  items: T[];
  showOrderActions?: boolean;
  showDeleteActions?: boolean;
  identifier?: string;
  primaryTextKey: string;
  secondaryTextKey: string;
  inputItemNameKey: string;
  updateMutation: DocumentNode;
  renderLeftIcon: (item: T) => JSX.Element;
  renderEditDialog: (isOpen: boolean, onClose: () => void, selectedItem?: T) => JSX.Element;
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const EditableList = <T, _>(props: Props<T>): React.ReactElement | null => {
  const {
    title,
    items,
    showOrderActions = false,
    showDeleteActions = false,
    inputItemNameKey,
    updateMutation,
    identifier = 'id',
    itunesId,
    primaryTextKey,
    secondaryTextKey,
    renderLeftIcon,
    renderEditDialog,
  } = props;

  const [isDialogOpen, setDialogOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState<T | undefined>();
  const [updateItem] = useMutation(updateMutation);

  const listItems = items.map(
    (i: T) =>
      ({
        leftIcon: renderLeftIcon(i),
        // @ts-ignore
        primaryText: i[primaryTextKey],
        // @ts-ignore
        secondaryText: i[secondaryTextKey],
        actionIcon: (
          <ActionMenu
            showDeleteActions={showDeleteActions}
            showOrderActions={showOrderActions}
            onAction={(a: ActionType) => hanldeAction(a, i)}
          />
        ),
      } as Item),
  );

  const hanldeAction = (action: ActionType, item: T) => {
    switch (action) {
      case 'EDIT':
        setSelectedItem(item);
        setDialogOpen(true);
        return;
      case 'DELETE':
        handleUpdate(item, true);
        return;
      case 'MOVE_UP':
        handleUpdate(item, false, MoveDirection.UP);
        return;
      case 'MOVE_DOWN':
        handleUpdate(item, false, MoveDirection.DOWN);
        return;
      default:
        return;
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedItem(undefined);
  };

  const handleUpdate = async (item: T, isDelete: boolean, direction?: MoveDirection) => {
    const input = {
      itunesId,
      // @ts-ignore
      [inputItemNameKey]: { [identifier]: item[identifier] },
      isDelete,
      move: direction,
    };
    try {
      await updateItem({ variables: { input } });
    } catch (e) {
      console.error(e.message);
    } finally {
      // TODO: add toast
    }
  };

  const renderCardActions = () => (
    <Button color="primary" onClick={() => setDialogOpen(true)}>
      Add {title}
    </Button>
  );

  const renderEmptyButton = () => (
    <Button variant="outlined" size="large" onClick={() => setDialogOpen(true)}>
      Add {title}
    </Button>
  );

  const renderLinksCard = () => (
    <ListCard title={title} actions={renderCardActions()} listItems={listItems} reverseLabels />
  );

  return (
    <>
      {!!items.length ? renderLinksCard() : renderEmptyButton()}
      {renderEditDialog(isDialogOpen, handleDialogClose, selectedItem)}
    </>
  );
};
